import { useForm } from "react-hook-form";

import type { AddressDto, CompanyDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { LocationCodeField } from "@/components/LocationCodeField/LocationCodeField";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { formatAddress } from "@/helpers/address";
import { createImageSizeRule, createImageTypeRule, createRequiredStringRule } from "@/helpers/rules";
import { useSlug } from "@/hooks/useSlug";
import { useProjectAppBrandingConfig } from "@/providers/ProjectAppBrandingConfigProvider";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface CompanyFormProps {
  isEditMode: boolean;
  defaultValues?: Partial<CompanyFormValues>;
  isSubmitting: boolean;
  existingCompanies: CompanyDto[];
  addresses: AddressDto[];
  locationCode?: string;
  isRefreshingLocationCode?: boolean;
  onRefreshLocationCode?: () => Promise<unknown>;
  onSubmit: (data: CompanyFormValues) => Promise<unknown>;
}

export function CompanyForm({
  addresses,
  existingCompanies,
  isEditMode,
  defaultValues,
  isSubmitting,
  locationCode,
  isRefreshingLocationCode,
  onRefreshLocationCode,
  onSubmit,
}: CompanyFormProps): React.ReactNode {
  const formMethods = useForm<CompanyFormValues>({ defaultValues });
  const slug = useSlug();
  const { t } = useTranslation();

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={isEditMode ? t("page.companies.edit.title") : t("page.companies.new.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.companies.list.title"),
              to: routes.companies.list({ slug }),
            },
            {
              name: isEditMode ? t("page.companies.edit.title") : t("page.companies.new.title"),
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
        <Form.Container>
          <CreateEditForm
            isEditMode={isEditMode}
            isSubmitting={isSubmitting}
            existingCompanies={existingCompanies}
            addresses={addresses}
            locationCode={locationCode}
            isRefreshingLocationCode={isRefreshingLocationCode}
            onRefreshLocationCode={onRefreshLocationCode}
          />
        </Form.Container>
      </Form.Root>
    </DocumentPage>
  );
}

function CreateEditForm({
  isEditMode,
  isSubmitting,
  addresses,
  existingCompanies,
  locationCode,
  isRefreshingLocationCode,
  onRefreshLocationCode,
}: {
  isEditMode: boolean;
  isSubmitting: boolean;
  addresses: CompanyFormProps["addresses"];
  existingCompanies: CompanyFormProps["existingCompanies"];
  locationCode: CompanyFormProps["locationCode"];
  isRefreshingLocationCode: CompanyFormProps["isRefreshingLocationCode"];
  onRefreshLocationCode: CompanyFormProps["onRefreshLocationCode"];
}) {
  const { t } = useTranslation();
  const { appBrandingConfig } = useProjectAppBrandingConfig();

  const existingCompanyNames = new Set(existingCompanies.map((x) => x.name.toString().trim().toLowerCase()));

  const showCompanyExternalId = appBrandingConfig.id === "healthDistrict";

  return (
    <>
      <Form.Body>
        <FormField label={t("page.companies.form.name")} required>
          <FormInput<CompanyFormValues, "name">
            name="name"
            rules={{
              validate: {
                required: createRequiredStringRule(t, "page.companies.form.name"),
                isDuplicate(value: string) {
                  if (existingCompanyNames.has(value.trim().toLowerCase())) {
                    return t("page.companies.form.name.error.duplicate");
                  }
                },
              },
            }}
          />
        </FormField>
        <FormField label={t("page.companies.form.description")}>
          <FormTextArea name="description" data-testid="company-description" />
        </FormField>
        {showCompanyExternalId && (
          <FormField label={t("page.companies.form.external-id")}>
            <FormInput<CompanyFormValues, "externalId"> name="externalId" data-testid="company-external-id" />
          </FormField>
        )}
        <FormField label={t("page.companies.form.address")} required>
          <FormSelect<CompanyFormValues, AddressDto>
            name="address"
            data-testid="company-address"
            items={addresses}
            placeholder={t("page.companies.form.address.placeholder")}
            keySelector={(x) => x.id || "undefined"}
            renderOption={(x) => formatAddress(x)}
            groupSelector={(x) => x.building.name}
            rules={{
              required: t("components.form.error.required", {
                inputName: t("page.companies.form.address"),
              }),
            }}
          />
        </FormField>
        <FormField label={t("page.companies.form.logo")}>
          <FormImageInput<CompanyFormValues, "logo">
            name="logo"
            data-testid="company-logo"
            rules={{
              validate: {
                size: createImageSizeRule({ t }),
                type: createImageTypeRule({ t }),
              },
            }}
          />
        </FormField>
        <FormField label={t("page.companies.form.background")}>
          <FormImageInput<CompanyFormValues, "background">
            name="background"
            rules={{
              validate: {
                size: createImageSizeRule({ t }),
                type: createImageTypeRule({ t }),
              },
            }}
          />
        </FormField>
        {isEditMode && onRefreshLocationCode && (
          <LocationCodeField
            value={locationCode}
            label={t("page.companies.list.location-code")}
            refreshLabel={t("page.companies.list.refresh-location-code")}
            modalTitle={t("page.companies.list.location-code.refresh.title")}
            modalDescription={t("page.companies.list.location-code.refresh.description")}
            confirmLabel={t("page.companies.list.location-code.refresh.actions.confirm")}
            cancelLabel={t("page.companies.list.location-code.refresh.actions.cancel")}
            isRefreshing={isRefreshingLocationCode ?? false}
            onRefresh={onRefreshLocationCode}
          />
        )}
      </Form.Body>
      <Form.Footer
        renderPrimaryAction={() => (
          <Button type="submit" isLoading={isSubmitting}>
            {isEditMode ? t("common.action.save") : t("common.action.create")}
          </Button>
        )}
      />
    </>
  );
}

export interface CompanyFormValues {
  name: string;
  description?: string;
  externalId?: string;
  logo: FormImage[];
  background: FormImage[];
  address: AddressDto;
}
